import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Chip, Typography } from "@mui/material";
import {
    SHOW_WORD_WHEEL_APP_STORE_BADGE,
    SHOW_WORD_WHEEL_GOOGLE_PLAY_BADGE,
    WORD_WHEEL_QUEST_APP_STORE_URL,
    WORD_WHEEL_QUEST_GOOGLE_PLAY_URL,
} from "../../config/nativeAppsVisibility";
import wordWheelQuestIcon from "../../assets/app-icons/word-wheel-quest-app-icon.png";
import LetterWheel, {
    buildWheelTiles,
    shuffleWheelTiles,
} from "../../prototype/mobile-word-wheel/components/LetterWheel";
import AppStoreBadgeLink from "../components/AppStoreBadgeLink";
import AppStorePromoPill from "../components/AppStorePromoPill";
import GooglePlayBadgeLink from "../components/GooglePlayBadgeLink";
import { usePagesTranslation } from "../locales";

/** Same palette as crossword play letter wheel. */
const CROSSWORD_WHEEL_COLORS = {
    text: "#0B3D2E",
    textOnSurface: "#0B3D2E",
    accent: "#3BA666",
    accentDark: "#1E5C3A",
    accentSoft: "rgba(255, 255, 255, 0.55)",
    accentRing: "rgba(11, 61, 46, 0.35)",
    border: "rgba(255, 255, 255, 0.85)",
    surface: "rgba(255, 255, 255, 0.96)",
    selectedSurface: "#ffffff",
    selectedText: "#0B3D2E",
    selectedBorder: "#0B3D2E",
    shadowSm: "0 2px 8px rgba(20, 50, 35, 0.28)",
    shadowMd: "0 4px 16px rgba(20, 50, 35, 0.35)",
    wheelLine: "#FFFFFF",
    wheelLineDark: "#E8FFF2",
};

/** Demo puzzle from Word Wheel Quest — 3×4 board (GOD / DOG / ODD). */
const DEMO_GRID_COLS = 3;
const DEMO_GRID_ROWS = 4;
/** `null` = unused cell; letter = puzzle cell. */
const DEMO_GRID_LETTERS = [
    [null, null, "O"],
    [null, "D", "D"],
    ["G", "O", "D"],
    [null, "G", null],
];
const DEMO_WORDS = [
    { word: "ODD", cells: [[0, 2], [1, 2], [2, 2]] },
    { word: "DOG", cells: [[1, 1], [2, 1], [3, 1]] },
    { word: "GOD", cells: [[2, 0], [2, 1], [2, 2]] },
];
const DEMO_WHEEL_LETTERS = ["G", "O", "D"];
const APP_NAME = "Word Wheel Quest";

const SECTION_HEADLINE_SX = {
    textAlign: "center",
    fontWeight: 800,
    fontSize: { xs: "1.55rem", sm: "1.9rem", md: "2.15rem" },
    lineHeight: 1.2,
    letterSpacing: "-0.03em",
    mb: { xs: 1.5, sm: 2 },
    background: "linear-gradient(118deg, #1e3a2f 0%, #2e7d32 42%, #3BA666 100%)",
    backgroundClip: "text",
    WebkitBackgroundClip: "text",
    color: "transparent",
};

function normalizeWord(raw) {
    return String(raw || "")
        .toUpperCase()
        .replace(/[^A-ZÀÂÄÉÈÊËÏÎÔÙÛÜŸÇÑ]/g, "");
}

const REVEAL_ANIM_MS = 720;

/** 3×4 Word Wheel puzzle board (unused cells gray; puzzle cells white). */
function DemoPuzzleGrid({ foundWords }) {
    const foundSet = useMemo(
        () => new Set((foundWords || []).map(normalizeWord)),
        [foundWords],
    );
    const prevFoundRef = useRef([]);
    const [flashWord, setFlashWord] = useState(null);
    const [flashKey, setFlashKey] = useState(0);

    useEffect(() => {
        const prev = prevFoundRef.current;
        const next = foundWords || [];
        const newlyFound = next.find((word) => !prev.includes(word));
        prevFoundRef.current = next;
        if (!newlyFound) return undefined;
        setFlashWord(newlyFound);
        setFlashKey((k) => k + 1);
        const timer = window.setTimeout(() => setFlashWord(null), REVEAL_ANIM_MS);
        return () => window.clearTimeout(timer);
    }, [foundWords]);

    const revealedCells = useMemo(() => {
        const cells = new Set();
        DEMO_WORDS.forEach(({ word, cells: wordCells }) => {
            if (!foundSet.has(word)) return;
            wordCells.forEach(([r, c]) => cells.add(`${r},${c}`));
        });
        return cells;
    }, [foundSet]);

    const flashCells = useMemo(() => {
        if (!flashWord) return new Map();
        const entry = DEMO_WORDS.find((w) => w.word === flashWord);
        if (!entry) return new Map();
        const map = new Map();
        entry.cells.forEach(([r, c], i) => map.set(`${r},${c}`, i));
        return map;
    }, [flashWord]);

    return (
        <Box
            role="img"
            aria-label="Word Wheel Quest 3 by 4 puzzle grid"
            sx={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxSizing: "border-box",
                "@keyframes wwCellReveal": {
                    "0%": {
                        transform: "scale(0.55)",
                        backgroundColor: "#86efac",
                        boxShadow: "0 0 0 0 rgba(59, 166, 102, 0.55)",
                        color: "#065f46",
                    },
                    "45%": {
                        transform: "scale(1.14)",
                        backgroundColor: "#bbf7d0",
                        boxShadow: "0 0 0 8px rgba(59, 166, 102, 0)",
                        color: "#065f46",
                    },
                    "100%": {
                        transform: "scale(1)",
                        backgroundColor: "#ffffff",
                        boxShadow: "0 0 0 0 rgba(59, 166, 102, 0)",
                        color: "#111827",
                    },
                },
                "@keyframes wwWordGlow": {
                    "0%": { boxShadow: "0 0 0 0 rgba(59, 166, 102, 0.45)" },
                    "40%": { boxShadow: "0 0 0 6px rgba(59, 166, 102, 0.2)" },
                    "100%": { boxShadow: "0 4px 14px rgba(15, 23, 42, 0.12)" },
                },
            }}
        >
            <Box
                sx={{
                    width: "100%",
                    maxWidth: 168,
                    aspectRatio: `${DEMO_GRID_COLS} / ${DEMO_GRID_ROWS}`,
                    display: "grid",
                    gridTemplateColumns: `repeat(${DEMO_GRID_COLS}, 1fr)`,
                    gridTemplateRows: `repeat(${DEMO_GRID_ROWS}, 1fr)`,
                    gap: "4px",
                    bgcolor: "#9ca3af",
                    p: "4px",
                    borderRadius: 1.5,
                    boxShadow: "0 4px 14px rgba(15, 23, 42, 0.12)",
                    animation: flashWord
                        ? `wwWordGlow ${REVEAL_ANIM_MS}ms ease-out`
                        : "none",
                }}
            >
                {Array.from({ length: DEMO_GRID_ROWS }, (_, row) =>
                    Array.from({ length: DEMO_GRID_COLS }, (_, col) => {
                        const letter = DEMO_GRID_LETTERS[row][col];
                        const isPuzzleCell = Boolean(letter);
                        const cellKey = `${row},${col}`;
                        const isRevealed = isPuzzleCell && revealedCells.has(cellKey);
                        const flashOrder = flashCells.get(cellKey);
                        const isFlashing = flashOrder != null;
                        return (
                            <Box
                                key={`${cellKey}-${isFlashing ? flashKey : "steady"}`}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    minWidth: 0,
                                    minHeight: 0,
                                    borderRadius: 0.75,
                                    bgcolor: isPuzzleCell
                                        ? isRevealed
                                            ? "#ffffff"
                                            : "rgba(255,255,255,0.55)"
                                        : "#9ca3af",
                                    border: isPuzzleCell
                                        ? `1.5px solid ${
                                            isFlashing
                                                ? "#3BA666"
                                                : "rgba(55, 65, 81, 0.55)"
                                        }`
                                        : "none",
                                    color: isRevealed ? "#111827" : "transparent",
                                    fontWeight: 800,
                                    fontSize: "1rem",
                                    fontFamily:
                                        '"Segoe UI", system-ui, -apple-system, sans-serif',
                                    lineHeight: 1,
                                    userSelect: "none",
                                    zIndex: isFlashing ? 2 : 1,
                                    ...(isFlashing
                                        ? {
                                              animation: `wwCellReveal ${REVEAL_ANIM_MS}ms cubic-bezier(0.22, 1.2, 0.36, 1) both`,
                                              animationDelay: `${flashOrder * 70}ms`,
                                          }
                                        : isRevealed
                                            ? {
                                                  transition:
                                                      "background-color 0.2s ease, border-color 0.2s ease",
                                              }
                                            : null),
                                }}
                            >
                                {isRevealed ? letter : null}
                            </Box>
                        );
                    }),
                )}
            </Box>
        </Box>
    );
}

/**
 * Landing showcase for Word Wheel Quest — 3×4 grid + dial + Google Play promo.
 */
export default function LandingWordWheelSection({ embedded = false } = {}) {
    const { t, lang } = usePagesTranslation();

    const [tiles, setTiles] = useState(() =>
        shuffleWheelTiles(buildWheelTiles(DEMO_WHEEL_LETTERS, `landing-${lang}-init`)),
    );
    const [selectedIndices, setSelectedIndices] = useState([]);
    const [foundWords, setFoundWords] = useState([]);
    const selectedIndicesRef = useRef([]);
    const tilesRef = useRef(tiles);

    tilesRef.current = tiles;

    useEffect(() => {
        setTiles(shuffleWheelTiles(buildWheelTiles(DEMO_WHEEL_LETTERS, `landing-${lang}`)));
        setSelectedIndices([]);
        selectedIndicesRef.current = [];
        setFoundWords([]);
    }, [lang]);

    useEffect(() => {
        selectedIndicesRef.current = selectedIndices;
    }, [selectedIndices]);

    const clearSelection = useCallback(() => {
        setSelectedIndices([]);
        selectedIndicesRef.current = [];
    }, []);

    const setSelection = useCallback((updater) => {
        setSelectedIndices((prev) => {
            const next = typeof updater === "function" ? updater(prev) : updater;
            selectedIndicesRef.current = next;
            return next;
        });
    }, []);

    const tryCommitSpelled = useCallback((indices) => {
        const spelled = indices
            .map((i) => tilesRef.current[i]?.letter)
            .filter(Boolean)
            .join("");
        const match = DEMO_WORDS.find((w) => w.word === spelled);
        if (!match) return false;
        setFoundWords((prev) => (prev.includes(match.word) ? prev : [...prev, match.word]));
        return true;
    }, []);

    const handleDragEnd = useCallback(() => {
        tryCommitSpelled(selectedIndicesRef.current);
        clearSelection();
    }, [tryCommitSpelled, clearSelection]);

    return (
        <Box
            component={embedded ? "div" : "section"}
            aria-labelledby="landing-word-wheel-heading"
            sx={{
                mb: embedded ? 0 : { xs: 4, sm: 5 },
                maxWidth: 920,
                mx: "auto",
                width: "100%",
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    alignItems: "center",
                    gap: { xs: 3, sm: 4 },
                    px: { xs: 2.5, sm: 3.5 },
                    py: { xs: 3.5, sm: 4 },
                    borderRadius: 3,
                    background:
                        "linear-gradient(145deg, rgba(232, 245, 233, 0.55) 0%, rgba(255,255,255, 0.62) 55%, rgba(241, 248, 233, 0.5) 100%)",
                    backdropFilter: "blur(10px)",
                    border: "1px solid rgba(255, 255, 255, 0.55)",
                    boxShadow: "0 12px 40px rgba(27, 94, 32, 0.08), 0 2px 10px rgba(0,0,0,0.03)",
                }}
            >
                <Box
                    sx={{
                        width: { xs: 220, sm: 240 },
                        flexShrink: 0,
                        mx: "auto",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 1.5,
                    }}
                >
                    <DemoPuzzleGrid foundWords={foundWords} />
                    <Box sx={{ width: "100%", height: { xs: 180, sm: 200 } }}>
                        <LetterWheel
                            tiles={tiles}
                            selectedIndices={selectedIndices}
                            onSelectionChange={setSelection}
                            onDragEnd={handleDragEnd}
                            colors={CROSSWORD_WHEEL_COLORS}
                            backdrop="gradient"
                            edgeInset={1}
                            fill
                        />
                    </Box>
                </Box>

                <Box sx={{ flex: 1, textAlign: { xs: "center", sm: "left" }, minWidth: 0 }}>
                    <Chip
                        label={t("landing.word-wheel-section-badge", "On Google Play")}
                        size="small"
                        sx={{
                            mb: 1.25,
                            fontWeight: 700,
                            letterSpacing: "0.04em",
                            bgcolor: "rgba(59, 166, 102, 0.16)",
                            color: "#1b5e20",
                            border: "1px solid rgba(59, 166, 102, 0.35)",
                        }}
                    />
                    <Typography
                        id="landing-word-wheel-heading"
                        component={embedded ? "h3" : "h2"}
                        sx={{
                            ...SECTION_HEADLINE_SX,
                            textAlign: { xs: "center", sm: "left" },
                            mb: 1.25,
                        }}
                    >
                        {t("landing.word-wheel-section-headline", APP_NAME)}
                    </Typography>
                    <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{
                            mb: 2,
                            fontSize: { xs: "1.05rem", sm: "1.125rem" },
                            lineHeight: 1.55,
                            maxWidth: 520,
                            mx: { xs: "auto", sm: 0 },
                        }}
                    >
                        {t(
                            "landing.word-wheel-section-body",
                            "Spin the dial to spell words onto the 3×4 grid and build your puzzle points. Play the full app on the App Store and Google Play.",
                        )}
                    </Typography>
                    {SHOW_WORD_WHEEL_GOOGLE_PLAY_BADGE || SHOW_WORD_WHEEL_APP_STORE_BADGE ? (
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: { xs: "center", sm: "flex-start" },
                            }}
                        >
                            <AppStorePromoPill
                                iconSrc={wordWheelQuestIcon}
                                appName={APP_NAME}
                                tagline={t(
                                    "landing.word-wheel-section-tagline",
                                    "Spin. Find. Score.",
                                )}
                                sx={{ maxWidth: "100%" }}
                            >
                                {SHOW_WORD_WHEEL_APP_STORE_BADGE ? (
                                    <AppStoreBadgeLink
                                        height={40}
                                        href={WORD_WHEEL_QUEST_APP_STORE_URL}
                                        ariaLabel={t(
                                            "landing.word-wheel-app-store-badge-aria",
                                            "Get Word Wheel Quest on the App Store (opens in a new tab)",
                                        )}
                                    />
                                ) : null}
                                {SHOW_WORD_WHEEL_GOOGLE_PLAY_BADGE ? (
                                    <GooglePlayBadgeLink
                                        height={40}
                                        href={WORD_WHEEL_QUEST_GOOGLE_PLAY_URL}
                                        ariaLabel={t(
                                            "landing.word-wheel-google-play-badge-aria",
                                            "Get Word Wheel Quest on Google Play (opens in a new tab)",
                                        )}
                                    />
                                ) : null}
                            </AppStorePromoPill>
                        </Box>
                    ) : null}
                </Box>
            </Box>
        </Box>
    );
}
